package fyi.moko.hotpl.ui.friends

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.filled.Call
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.Place
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.SuggestionChip
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import fyi.moko.hotpl.domain.model.Friend
import fyi.moko.hotpl.domain.model.FriendSharedPlace
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.ui.components.PhotoLoad
import fyi.moko.hotpl.ui.components.rememberSharedPhoto
import fyi.moko.hotpl.ui.detail.PlaceCategoryImage

/**
 * A friend's shared place, looked at before deciding to keep it (backlog 0-36 d). Everything
 * the projection carries and nothing else: cover photo, name, the friend's own status, tags,
 * business contact, a map link. One action at the bottom — add it to my journal as a wish.
 *
 * 사진은 여기서만 본다. 담을 때 복사하지 않는다 — 내 저널에 친구 사진이 내 사진처럼
 * 섞이면 나중에 누구 것인지 알 수 없다.
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun FriendPlacePreview(
    friend: Friend,
    place: FriendSharedPlace,
    inJournal: Boolean,
    addInProgress: Boolean,
    onBack: () -> Unit,
    onAddToJournal: () -> Unit,
) {
    val uriHandler = LocalUriHandler.current
    Column(modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState())) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            IconButton(onClick = onBack) {
                Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back to ${friend.displayName}'s places")
            }
            Text(
                friend.displayName,
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }

        // The projection lists the cover first, then up to three food photos (slice B). The
        // big frame shows whichever one was tapped last; the strip below only appears when
        // there is more than one — a lone thumbnail under the same picture says nothing.
        var shown by remember(place.placeId) { mutableIntStateOf(0) }
        SharedCoverImage(
            ownerUid = place.ownerId,
            fileName = place.photos.getOrNull(shown),
            tags = place.tags,
            contentDescription = place.name,
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .aspectRatio(4f / 3f)
                .clip(RoundedCornerShape(16.dp)),
        )
        if (place.photos.size > 1) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                place.photos.forEachIndexed { index, fileName ->
                    val selected = index == shown
                    SharedCoverImage(
                        ownerUid = place.ownerId,
                        fileName = fileName,
                        tags = place.tags,
                        contentDescription = if (index == 0) "Cover photo" else "Food photo ${index}",
                        modifier = Modifier
                            .size(64.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .then(
                                if (selected) Modifier.border(2.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(10.dp))
                                else Modifier
                            )
                            .clickable { shown = index },
                    )
                }
            }
        }

        Column(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(place.name, style = MaterialTheme.typography.headlineSmall)
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                Text(
                    friendStatusLine(friend.displayName, place),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (place.liked) {
                    Icon(
                        Icons.Default.Favorite,
                        contentDescription = "Liked",
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            if (place.tags.isNotEmpty()) {
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    place.tags.forEach { tag ->
                        SuggestionChip(onClick = {}, label = { Text(tag) })
                    }
                }
            }

            place.phoneNumber?.takeIf { it.isNotBlank() }?.let { phone ->
                ContactRow(icon = Icons.Default.Call, text = phone) {
                    uriHandler.openUri("tel:${phone.filter { !it.isWhitespace() }}")
                }
            }
            place.website?.takeIf { it.isNotBlank() }?.let { site ->
                ContactRow(icon = Icons.AutoMirrored.Filled.ExitToApp, text = site) { uriHandler.openUri(site) }
            }
            if (place.latitude != null && place.longitude != null) {
                ContactRow(icon = Icons.Default.Place, text = "Open in Maps") {
                    uriHandler.openUri(
                        "https://www.google.com/maps/search/?api=1&query=${place.latitude},${place.longitude}"
                    )
                }
            }

            Spacer(Modifier.height(8.dp))
            when {
                inJournal -> OutlinedButton(onClick = {}, enabled = false, modifier = Modifier.fillMaxWidth()) {
                    Text("In your journal")
                }
                addInProgress -> Button(onClick = {}, enabled = false, modifier = Modifier.fillMaxWidth()) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                }
                else -> Button(onClick = onAddToJournal, modifier = Modifier.fillMaxWidth()) {
                    Text("Add to Want to go")
                }
            }
            Text(
                "Saved as a place you want to go. Their photos stay theirs — yours come when you visit.",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
    }
}

/** The friend's own record of the place, in one line: it is theirs, not a fact about me. */
private fun friendStatusLine(name: String, place: FriendSharedPlace): String {
    val verb = when (place.status) {
        PlaceStatus.VISITED -> "$name has been here"
        PlaceStatus.WISHLIST -> "$name wants to go"
        PlaceStatus.DISMISSED -> "$name saved this"
    }
    return verb
}

@Composable
private fun ContactRow(icon: androidx.compose.ui.graphics.vector.ImageVector, text: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick).padding(vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Icon(icon, contentDescription = null, tint = MaterialTheme.colorScheme.primary, modifier = Modifier.size(20.dp))
        Text(text, style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.primary)
    }
}

/**
 * The shared cover, or the category illustration when there is none (or it can't be fetched).
 * Shared by the preview header and the list thumbnail so both fall back the same way.
 */
@Composable
internal fun SharedCoverImage(
    ownerUid: String,
    fileName: String?,
    tags: List<String>,
    contentDescription: String?,
    modifier: Modifier = Modifier,
) {
    val handle = rememberSharedPhoto(ownerUid, fileName)
    when (val photo = handle.photo) {
        is PhotoLoad.Loaded -> Image(
            bitmap = photo.value,
            contentDescription = contentDescription,
            contentScale = ContentScale.Crop,
            modifier = modifier,
        )
        PhotoLoad.Fetching, PhotoLoad.Loading -> Box(
            modifier = modifier.background(MaterialTheme.colorScheme.surfaceVariant),
            contentAlignment = Alignment.Center,
        ) {
            CircularProgressIndicator(modifier = Modifier.size(20.dp), strokeWidth = 2.dp)
        }
        else -> PlaceCategoryImage(category = PlaceCategory.fromTags(tags), modifier = modifier)
    }
}
